/*
 * <vitesse-widget-preview> — a widget being written, shown in the tile it will live in.
 *
 * Not a bare frame. A widget is judged by how it sits in its tile — the card, the bar under it, the
 * title, and above all how it redraws as the tile is dragged bigger and smaller — so the preview is
 * the dashboard's own grid with one tile in it: the same twelve columns and row height, the same
 * resize ring, the same bar. Only what makes no sense off a board is left out: there is no trash,
 * because the tile is on no board to be removed from.
 *
 * The readings are the demo's made-up ones (widget-demo.ts), so a widget can be looked at and
 * resized before anything of the plant is handed to it.
 *
 * Under the tile it lists where the widget falls below the floor every widget must clear — a colour
 * that is not a token, NaN on screen, text outside the box (runtime/scene-check.ts) — the same rules
 * the built-in widgets are held to, so a widget being written can be fixed before it reaches a board.
 */
import { LitElement, html, css, nothing, type PropertyValues } from 'lit';
import { t } from '@vitesse/base-ui/i18n';
import { customElement, property, state } from 'lit/decorators.js';
import type { PanelDef, PanelLayoutItem } from '../main/shell/vitesse-panel-grid';
import type { DemoSeries } from './widget-demo';
import '../main/shell/vitesse-panel-grid';
import './widget-demo';

const TILE = 'preview';
/** Where a fresh preview starts: half the board wide, three rows tall — the size most tiles are placed at. */
const START: PanelLayoutItem = { id: TILE, x: 0, y: 0, w: 6, h: 3 };

@customElement('vitesse-widget-preview')
export class VitesseWidgetPreview extends LitElement {
  static styles = css`
    :host {
      display: block;
      width: 100%;
    }
    .problems {
      margin: var(--space-xs) var(--space-2xs) 0;
      padding: var(--space-xs) var(--space-sm);
      border: 1px solid var(--border-color);
      border-left: 3px solid var(--amber);
      border-radius: var(--radius-sm);
      font-size: var(--fs-xs);
      color: var(--text-secondary);
    }
    .problems strong {
      display: block;
      margin-bottom: var(--space-2xs);
      color: var(--text-primary);
      font-weight: var(--fw-semibold);
    }
    .problems ul {
      margin: 0;
      padding-left: var(--space-md);
    }
  `;

  @property({ attribute: false }) source = '';
  @property({ attribute: false }) config: Record<string, unknown> = {};
  @property({ attribute: false }) series: DemoSeries[] = [];
  /** The name in the tile's bar. */
  @property() heading = '';

  /** Kept across edits of the source, so a person who resized the tile keeps the size they chose. */
  @state() private layout: PanelLayoutItem[] = [START];
  /** Where the running source falls below the floor. Cleared when the source changes. */
  @state() private problems: string[] = [];

  protected willUpdate(changed: PropertyValues): void {
    if (changed.has('source')) this.problems = [];
  }

  private panelsFor(title: string): PanelDef[] {
    if (this.panelsCache?.title !== title) this.panelsCache = { title, panels: [{ ...START, title, minW: 1, minH: 1, noRemove: true }] };
    return this.panelsCache.panels;
  }
  /** The same array while the title holds, so the grid sees no change and rebuilds nothing. */
  private panelsCache?: { title: string; panels: PanelDef[] };

  render() {
    return html`<vitesse-panel-grid
        .panels=${this.panelsFor(this.heading)}
        .layout=${this.layout}
        @layout-change=${(e: CustomEvent<PanelLayoutItem[]>) => (this.layout = e.detail)}
        @widget-problems=${(e: CustomEvent<string[]>) => (this.problems = e.detail)}
      >
        <vitesse-widget-demo slot=${TILE} .source=${this.source} .config=${this.config} .series=${this.series}></vitesse-widget-demo>
      </vitesse-panel-grid>
      ${this.problems.length
        ? html`<div class="problems" role="status">
            <strong>${t('dashboards.previewProblems')}</strong>
            <ul>${this.problems.map((p) => html`<li>${p}</li>`)}</ul>
          </div>`
        : nothing}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-widget-preview': VitesseWidgetPreview;
  }
}
